import React from 'react';
import { useProgramStats, usePrograms } from '../../hooks/usePrograms';
import {
  ChartBarIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';

const ProgramStats: React.FC = () => {
  const { data: stats, isLoading, error } = useProgramStats();
  const { data: programs } = usePrograms();

  // Calculate AWIN-only programs (programs with missing admin data)
  const awinOnlyCount = React.useMemo(() => {
    if (!programs || !Array.isArray(programs)) return 0;

    return programs.filter(program => {
      const isIncomplete = !program.descriptionCustomer || !program.conditions || !program.cashbackRate || !program.countries?.length;
      return program.affiliateUrl && isIncomplete;
    }).length;
  }, [programs]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">Failed to load statistics</p>
      </div>
    );
  }

  const statCards = [
    {
      name: 'Total Programs',
      value: stats?.total || 0,
      icon: ChartBarIcon,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      name: 'Active Programs',
      value: stats?.active || 0,
      icon: EyeIcon,
      color: 'text-success-600 dark:text-success-400',
      bgColor: 'bg-success-100 dark:bg-success-900/30',
    },
    {
      name: 'Hidden Programs',
      value: stats?.hidden || 0,
      icon: EyeSlashIcon,
      color: 'text-gray-600 dark:text-gray-400',
      bgColor: 'bg-gray-100 dark:bg-gray-700',
    },
    {
      name: 'Needs Setup',
      value: awinOnlyCount,
      icon: ExclamationTriangleIcon,
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      name: 'Archived',
      value: stats?.archived || 0,
      icon: ArchiveBoxIcon,
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {statCards.map((stat) => (
        <div key={stat.name} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className={`p-2 rounded-lg ${stat.bgColor}`}>
              <stat.icon className={`h-5 w-5 ${stat.color}`} />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.name}</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">
                {stat.value.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProgramStats; 